import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'

import { PageHeader } from '../../components/PageHeader'
import { JobWatch } from '../../components/record/JobFlow'
import { PHASE_WORDS } from '../../components/record/phaseWords'
import { Button, Card, ConditionTiles, FileField, Icon, Notice, ProblemNotice, ProgressBar } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'
import { createUpload, submitRecordingJob, type Condition } from '../../lib/api'
import { webBluetoothAvailable } from '../../lib/headband/types'
import { newOperationKeys, sameSelection, type OperationKeys } from '../../lib/idempotency'
import { ApiError } from '../../lib/problem'
import {
  clearPendingJob,
  readPendingJob,
  writePendingJob,
  type PendingJob,
} from '../../lib/pendingJob'
import { CONDITION_WORDS } from '../../lib/recordings'
import { fileExceedsGrant, grantHasExpired, uploadToGrant } from '../../lib/upload'
import { isTerminalJob, useJobPolling } from '../../lib/useJobPolling'

/**
 * Record — the product records (Sprint 1.9.7).
 *
 * The page leads with the headband: `/record/connect` connects it over Web
 * Bluetooth, fits it against the head map, records three quiet minutes and hands
 * the session to processing itself. The person never sees a file.
 *
 * ── THE UPLOAD IS HOW A RECORDER SESSION ARRIVES ────────────────────────────
 *
 * Below the headband card, the archive upload of Sprint 1.9 is kept whole: an
 * archive goes in, a Daily Status comes out. Until the Pro records in the
 * browser, it is how a BrainBit Pro records at all: the recorder exports
 * `session.tar.gz` and it is uploaded here. So it is a section of the page,
 * never labelled "Testing only" (V1-INTEGRATION step 7), and never inside a
 * padded box: the job flow and the journal it opens need the page's full width
 * (the ten stress steps at 360px, `e2e/self-report.spec.ts`). It is also how the
 * mocked end-to-end suite drives the job path without a headband. Everything in
 * `ArchiveUpload` below is that flow, unchanged, and its comments are its own.
 *
 * ── THE WRITE ORDER IS THE WHOLE RECOVERY DESIGN ────────────────────────────
 *
 * `writePendingJob` happens **before** `POST /recordings/jobs`, not after. The
 * failure this exists for is a submission whose response never arrives: the
 * server created the job, the browser never learned its id, and the user
 * reloads. If the pointer were written from the response, there would be
 * nothing on disk to recover from — and the only available move would be to
 * submit again, which is how one recording becomes two in a baseline.
 *
 * Written first, the pointer holds the job's idempotency key, and replaying that
 * key returns the job that already exists. §2.8 in one sentence.
 *
 * ── AN `empty` RECORDING ARRIVES AS A FAILED JOB ────────────────────────────
 *
 * A recording with no scoreable signal produces a job whose status is
 * **`failed`**, whose `error_problem.code` is `recording.empty`, and which has
 * **no `result_recording_id`**. A terminal branch written on `job.status` would
 * render "failed" for exactly the case acceptance criterion 8 says must never be
 * rendered as a failure. So the code is read before the status, always — in
 * `components/record/JobFlow.tsx`, which both this page and the capture flow use.
 *
 * ── AND THE SUBMISSION RESPONSE IS ONLY A JOB ID ────────────────────────────
 *
 * A replayed `Idempotency-Key` returns the ORIGINAL stored 202 body — `pending`,
 * `progress_pct: 0`, no phase — however far the job has actually got. So nothing
 * here renders the submission's body: it yields an id, and `useJobPolling`'s
 * first `GET` supplies the state.
 */

export function Record() {
  const supported = webBluetoothAvailable()
  return (
    <div className="stack stack--standard">
      {/* The eyebrow repeats the `<h1>` verbatim, so it is decoration (R25) —
          `aria-hidden`, not a second announcement of the word "Record". */}
      <PageHeader
        eyebrow="Record"
        eyebrowHidden
        title="Record"
        lead="Connect your headband, fit it, and record three quiet minutes with your eyes closed. It is checked, scored and added to your baseline here."
      />

      <Card eyebrow="Record with your headband" active className="record-headband">
        <div className="record-headband__body">
          <div className="record-headband__ring" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M4 13a8 8 0 0 1 16 0v3a2 2 0 0 1-2 2h-1v-5h3M4 13v3a2 2 0 0 0 2 2h1v-5H4" />
            </svg>
          </div>
          <div className="record-headband__text">
            <h2 className="record-headband__title">Three minutes, eyes closed</h2>
            <p className="product-empty__body">
              Works with the BrainBit Pro (8 contacts) and the BrainBit (4 contacts): the page shows
              every contact on a head map, says which one to adjust, and starts only when the signal
              is steady. Nothing is saved to a file. If a headband will not connect, record it with the
              recorder and upload its session below.
            </p>
          </div>
          <Link className="btn btn--primary" to="/record/connect">
            <Icon name="record" />
            Connect your headband
          </Link>
        </div>
        {supported ? (
          <p className="record-headband__support">
            Uses Bluetooth in this browser. Pairing happens once; after that the headband reconnects
            on its own.
          </p>
        ) : (
          <Notice tone="warn">
            This browser cannot talk to the headband. Recording needs Web Bluetooth, which Chrome
            and Edge provide on a computer or an Android phone; Safari and Firefox do not. Open
            BrainSpot in Chrome to record.
          </Notice>
        )}
      </Card>

      <section className="stack subgrid" aria-labelledby="record-archive-title">
        <div className="record-archive__head">
          <h2 id="record-archive-title" className="record-archive__title">
            Upload a session from the recorder
          </h2>
          <p className="product-empty__body record-archive__note">
            Recording with a BrainBit Pro? Until the Pro records in the browser, record it with the
            recorder, then upload the session archive the recorder saved. It is checked, scored and
            added to your baseline the same way.
          </p>
        </div>
        <ArchiveUpload />
      </section>
    </div>
  )
}

/** §2.7. The canonical session archive, and nothing else. */
const ACCEPTED_EXTENSIONS = ['.tar', '.tar.gz'] as const

/**
 * `accept` is a HINT and is deliberately generous.
 *
 * iOS has no reliable UTI for `.tar.gz`, so a strict list greys out the file the
 * user actually wants. The extension is re-checked after selection, with a
 * message — which is also why Playwright can hand this input a `.zip`.
 */
const ACCEPT_ATTR = '.tar,.tar.gz,.tgz,application/x-tar,application/gzip,application/x-gzip'

function extensionIsAccepted(name: string): boolean {
  const lower = name.toLowerCase()
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

type FlowStep =
  /** Nothing in flight. The form is the screen. */
  | { step: 'idle' }
  /** Asking for a grant. */
  | { step: 'requesting' }
  /** Sending bytes to the bucket. `percent` is null until the first event. */
  | { step: 'uploading'; percent: number | null }
  /** The bytes are in the bucket; asking for a job. */
  | { step: 'submitting' }
  /** A job id is known. `useJobPolling` owns what happens next. */
  | { step: 'watching' }

function ArchiveUpload() {
  const { account } = useAuth()
  const userId = account?.user_id ?? null

  const [condition, setCondition] = useState<Condition | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [flow, setFlow] = useState<FlowStep>({ step: 'idle' })
  /**
   * A failure to report, as a SENTENCE plus the error that caused it.
   *
   * It was a bare string, built by a `messageFor()` that returned
   * `problem.detail || problem.title` and dropped `problem.code` -- 250 lines
   * from the `Reference: <code>` this same file renders for a terminal job.
   * §7.1 asks for the message AND the code, so the code has to survive to the
   * render.
   *
   * `error` is absent rather than null for the cases this screen explains
   * ITSELF: a dropped connection mid-upload, or the 15s client timeout, where
   * the pointer's existence decides the instruction and no server said
   * anything. Those keep a plain notice; there is no Problem to show and
   * inventing one would be worse than the string was.
   */
  const [failure, setFailure] = useState<{ text: string; error?: unknown } | null>(null)
  const [pending, setPending] = useState<PendingJob | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)

  /**
   * The two keys, in a ref rather than in state.
   *
   * They are minted on exactly three events — a new file, a changed condition,
   * an expired grant — and reused by every transport retry in between. A render
   * must never mint one: that is what turns a retry into a second operation, and
   * for `POST /uploads` there is no error anywhere to say so, because
   * `UploadRequest` carries only `user_id` and the fingerprint is constant.
   */
  /**
   * LAZY, and that is not a micro-optimisation.
   *
   * `useRef(newOperationKeys())` evaluates its argument on EVERY render — React
   * keeps only the first value, but the call still happens — so the screen minted
   * a fresh pair of UUIDs on every keystroke and threw them away. Harmless in
   * itself, and it made the one thing worth counting uncountable: "was a new
   * operation started?" is exactly "was `newOperationKeys` called?", and it was
   * called constantly. Found by a mutation that should have died and did not.
   */
  const keys = useRef<OperationKeys | null>(null)
  if (keys.current === null) keys.current = newOperationKeys()
  /** Whether THIS attempt got as far as writing the resume pointer. */
  const wrotePointer = useRef(false)
  /**
   * One attempt at a time, enforced rather than assumed.
   *
   * A ref, not state, because it has to be readable synchronously at the top of
   * `start()` — by the time a state update has rendered, the second click has
   * already run. The upload leg lasts minutes, and every duplicate-prevention
   * argument on this screen rests on one mutable key pair; two overlapping
   * attempts can mint over each other's keys and submit the same archive twice.
   */
  const inFlight = useRef(false)
  const lastSelection = useRef<File | null>(null)
  const lastCondition = useRef<Condition | null>(null)

  const poll = useJobPolling(jobId)

  /* ── resuming ─────────────────────────────────────────────────────────── */

  /**
   * The pointer is read once per signed-in user, DURING RENDER.
   *
   * A render-time adjustment rather than an effect, which is this codebase's
   * answer to "derive state when an input changes" (`AppShell`'s drawer,
   * `useResource`'s reload) and what `react-hooks/set-state-in-effect` is
   * pointing at. An effect would run a frame later — long enough to paint the
   * empty upload form to somebody who has a job in flight, inviting them to
   * upload the same session a second time.
   *
   * Reading `localStorage` during render is a pure read; nothing is written here.
   */
  const [hydratedFor, setHydratedFor] = useState<string | null>(null)
  const [resumeOf, setResumeOf] = useState<PendingJob | null>(null)
  if (userId !== null && hydratedFor !== userId) {
    setHydratedFor(userId)
    const found = readPendingJob(userId)
    if (found !== null) {
      setPending(found)
      if (found.jobId !== undefined) {
        setJobId(found.jobId)
        setFlow({ step: 'watching' })
      } else {
        // ONLY a pointer found on disk is a lost response to replay. A pointer
        // this session just wrote is one whose submission is still in flight (or
        // has just failed in `start`, which owns the message) -- replaying that
        // one would fire a second request while the first is still outstanding.
        setResumeOf(found)
      }
    }
  }

  /**
   * A pointer with NO job id is a submission whose response was lost.
   *
   * Replaying its key returns the job that already exists. Minting a fresh key
   * here would create a second job for the same upload — the exact duplicate
   * acceptance criterion 7 forbids.
   */
  const [replayAttempt, setReplayAttempt] = useState(0)
  const [replayStalled, setReplayStalled] = useState(false)
  const retryReplay = useCallback(() => {
    setReplayStalled(false)
    setReplayAttempt((n) => n + 1)
  }, [])

  useEffect(() => {
    if (userId === null || resumeOf === null) return
    const pointer = resumeOf
    let cancelled = false
    void (async () => {
      try {
        const job = await submitRecordingJob({
          userId,
          uploadId: pointer.uploadId,
          // The POINTER's condition, never the control's. The fingerprint is over
          // the resolved body, so replaying the stored key with a changed
          // condition is a 409 `idempotency.conflict`.
          condition: pointer.condition,
          // And the pointer's headband, exactly: the one the ORIGINAL submission
          // sent (a capture resumed here), or none at all when it sent none.
          ...(pointer.deviceId !== undefined ? { deviceId: pointer.deviceId } : {}),
          // And its pair link (the eyes-closed block of a paired session), for the same reason.
          ...(pointer.pair !== undefined ? { pair: pointer.pair } : {}),
          ...(pointer.experiment !== undefined ? { experiment: pointer.experiment } : {}),
          idempotencyKey: pointer.jobKey,
        })
        if (cancelled) return
        const resumed = { ...pointer, jobId: job.job_id }
        writePendingJob(userId, resumed)
        setPending(resumed)
        setJobId(job.job_id)
        setFlow({ step: 'watching' })
      } catch (err) {
        if (cancelled) return
        // An ApiError means the server answered. Anything else leaves the
        // submission's fate unknown, and the pointer stays so the next reload
        // can replay the same key again.
        // ── ONLY A REFUSAL THAT PROVES THE KEY IS DEAD DROPS THE POINTER ────
        //
        // A 5xx says nothing about whether the job was created, and neither does
        // a lost connection. Deleting the pointer for either throws away the one
        // handle on a job that may well be running, and the only thing left to
        // do is upload the archive again -- which is the duplicate this whole
        // mechanism exists to prevent.
        const definitive =
          err instanceof ApiError && [400, 403, 404, 409].includes(err.status)
        if (definitive) {
          clearPendingJob(userId)
          setPending(null)
          setResumeOf(null)
          setFailure({ text: 'That recording could not be picked up again.', error: err })
        } else {
          setReplayStalled(true)
        }
        setFlow({ step: 'idle' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId, resumeOf, replayAttempt])

  /**
   * A poll that refuses is a dead pointer, and a dead pointer strands the screen.
   *
   * A 404 for a job from another environment, a purged row, a 403 — `useJobPolling`
   * sets `error`, stops, and never runs again. Without this the pointer survives
   * every reload and reproduces the same dead end for ever.
   */
  useEffect(() => {
    if (poll.error === null || userId === null) return
    // Only the external write happens here. Everything the SCREEN does about it
    // is derived below from `poll.error` itself — which is what keeps this an
    // effect that updates an external system from state, rather than an effect
    // that sets state and cascades a render.
    clearPendingJob(userId)
  }, [poll.error, userId])

  /**
   * A poll that refused leaves nothing to watch. The form comes back, and the
   * pointer is gone from storage, so a reload does not reproduce the dead end.
   */
  const pollRefused = poll.error !== null

  // The job has stopped. The pointer has done its work, and leaving it would
  // resume a finished job on the next visit.
  useEffect(() => {
    if (userId === null || poll.job === null) return
    if (!isTerminalJob(poll.job.status)) return
    clearPendingJob(userId)
  }, [userId, poll.job])

  /* ── composing a new upload ───────────────────────────────────────────── */

  const resetOperation = useCallback(() => {
    keys.current = newOperationKeys()
  }, [])

  const pickFile = useCallback(
    (next: File | null) => {
      setFailure(null)
      // A NEW FILE IS A NEW OPERATION. Reusing the upload key would replay the
      // previous grant, which points at the previous file's key in the bucket.
      if (!sameSelection(lastSelection.current, next)) resetOperation()
      lastSelection.current = next
      setFile(next)
      setFileError(
        next !== null && !extensionIsAccepted(next.name)
          ? `That is not a session archive. Choose a ${ACCEPTED_EXTENSIONS.join(' or ')} file.`
          : null,
      )
    },
    [resetOperation],
  )

  const pickCondition = useCallback(
    (next: Condition) => {
      setFailure(null)
      if (lastCondition.current !== next) resetOperation()
      lastCondition.current = next
      setCondition(next)
    },
    [resetOperation],
  )

  // NOT `&& !pollRefused`. That term was there to re-expose the form after a
  // refused poll, but it also made `busy` false for the whole of the next
  // attempt -- `poll.error` cannot clear while `jobId` is unchanged -- leaving
  // the file and condition controls live through a minutes-long upload.
  const busy = flow.step !== 'idle'
  const resuming = pending !== null && !pollRefused
  /** A pointer found on DISK with no job id: a lost response, being replayed. */
  const recovering = resumeOf !== null && jobId === null && !pollRefused
  const canSubmit =
    userId !== null && condition !== null && file !== null && fileError === null && !busy && !resuming

  const start = useCallback(async () => {
    if (userId === null || condition === null || file === null) return
    // The guard, before anything else. A second click during the upload leg is
    // the realistic way two attempts overlap.
    if (inFlight.current) return
    inFlight.current = true
    setFailure(null)
    wrotePointer.current = false

    try {
      setFlow({ step: 'requesting' })
      let grant = await createUpload({ userId, idempotencyKey: keys.current!.upload })

      // A replayed key hands back the ORIGINAL grant, expired or not, with no
      // error anywhere. An expired grant is a new operation, not a retry.
      if (grantHasExpired(grant)) {
        resetOperation()
        grant = await createUpload({ userId, idempotencyKey: keys.current!.upload })
      }
      // Captured AFTER the last legitimate reset and used for the rest of this
      // attempt. Reading `keys.current` across the awaits below would let a reset
      // elsewhere re-point an operation already in flight.
      const ops = keys.current!

      if (fileExceedsGrant(file, grant)) {
        // A different file is a different operation, so the keys go with it.
        resetOperation()
        setFlow({ step: 'idle' })
        setFileError(
          'That archive is larger than this upload allows. Export a shorter session — the file is never trimmed to fit.',
        )
        return
      }

      setFlow({ step: 'uploading', percent: null })
      await uploadToGrant(grant, file, {
        onProgress: ({ percent }) => setFlow({ step: 'uploading', percent }),
      })

      // ── BEFORE the submission, deliberately. See the header. ──────────────
      wrotePointer.current = true
      const pointer: PendingJob = {
        uploadId: grant.upload_id,
        condition,
        jobKey: ops.job,
        startedAt: new Date().toISOString(),
      }
      writePendingJob(userId, pointer)
      setPending(pointer)

      setFlow({ step: 'submitting' })
      const job = await submitRecordingJob({
        userId,
        uploadId: grant.upload_id,
        condition,
        idempotencyKey: ops.job,
      })

      // The response is a source of `job_id` and nothing else: a replay returns
      // the body as it was at creation, whatever the job has done since.
      const accepted = { ...pointer, jobId: job.job_id }
      writePendingJob(userId, accepted)
      setPending(accepted)
      setJobId(job.job_id)
      setFlow({ step: 'watching' })
    } catch (err) {
      if (err instanceof ApiError) {
        setFailure({ text: 'That upload could not be accepted.', error: err })
        setFlow({ step: 'idle' })
        return
      }
      // The request's fate is unknown — a dropped connection, or the client's own
      // 15s timeout, which arrives as an AbortError and not as an ApiError.
      //
      // WHERE it failed decides what to say, and the difference is the pointer.
      // Before the submission there is nothing on disk and nothing was accepted,
      // so the honest instruction is "try again" — and pressing the button again
      // reuses the SAME keys, which is what makes it a retry rather than a second
      // operation. After the submission the pointer exists, and a reload replays
      // its key to recover the job that may well already be running.
      setFailure({
        text: wrotePointer.current
          ? 'We lost the connection after your recording was handed over. Nothing has been lost — reload this page and it will pick up where it left off.'
          : 'The upload did not finish. Your file is still selected; try again.',
      })
      setFlow({ step: 'idle' })
    } finally {
      inFlight.current = false
    }
  }, [userId, condition, file, resetOperation])

  const startOver = useCallback(() => {
    if (userId !== null) clearPendingJob(userId)
    setPending(null)
    setResumeOf(null)
    setReplayStalled(false)
    setJobId(null)
    setFlow({ step: 'idle' })
    setFailure(null)
    setFile(null)
    lastSelection.current = null
    resetOperation()
  }, [userId, resetOperation])

  return (
    <div className="stack record-upload subgrid">
      {failure !== null &&
        // `!== undefined`, not `'error' in failure`: the property is optional,
        // so an explicit `undefined` satisfies `in` and would take the
        // server-refusal branch for a case this screen means to explain itself.
        // No site does that today; nothing stops one.
        (failure.error !== undefined ? (
          <ProblemNotice error={failure.error}>{failure.text}</ProblemNotice>
        ) : (
          <Notice tone="error">{failure.text}</Notice>
        ))}

      {pollRefused && (
        <ProblemNotice error={poll.error}>
          That upload could not be followed any further.
        </ProblemNotice>
      )}

      {(jobId === null || pollRefused) && !resuming && (
        /*
          ── DOM ORDER IS WHAT A PHONE AND A SCREEN READER GET ──────────────
          Explanation, then the form, then what happens next. From 1024px the
          grid re-places the two explanatory cards into a second column
          (§2.7 / R23 keeps "Before you start" ABOVE the form below that), and
          neither of them contains anything focusable, so tab order does not
          move with them.
        */
        <div className="record-flow">
          <Card eyebrow="Before you start" className="record-flow__before">
            <p className="product-empty__body">
              <strong>BrainBit Pro, eight channels.</strong> A session archive —{' '}
              <code className="mono-cell">.tar</code> or{' '}
              <code className="mono-cell">.tar.gz</code> — with <code className="mono-cell">meta.json</code>{' '}
              and <code className="mono-cell">signal.parquet</code> at its root. Not loose EEG files,
              and not a ZIP renamed to look like one.
            </p>
            <p className="product-empty__body">
              The server checks the archive after it arrives. Nothing is ever trimmed or
              rewritten to make it fit.
            </p>
            {/* The device fact travels with the explanation, not with the
                controls: it is something to know before choosing, and it is
                not a field. */}
            <dl className="detail-grid">
              <div className="detail-grid__item">
                <dt className="record__label">Device</dt>
                <dd className="record__value">BrainBit Pro</dd>
              </div>
            </dl>
          </Card>

          <div className="record-steps">
            <Card className="step">
              <div className="step__head">
                <span className="step__num" aria-hidden="true">01</span>
                <span className="step__title">Condition</span>
              </div>
              {/* `hideLabel`, because the step title already says the word. The
                  span stays in the DOM and keeps naming the group. */}
              <ConditionTiles
                label="Condition"
                hideLabel
                value={condition}
                onChange={pickCondition}
                disabled={busy}
              />
              <p className="field__hint step__hint">
                Eyes closed and eyes open build separate baselines. Choose the one you
                actually recorded — they are never mixed.
              </p>
            </Card>

            <Card className="step">
              <div className="step__head">
                <span className="step__num" aria-hidden="true">02</span>
                <span className="step__title">Session archive</span>
              </div>
              <FileField
                label="Session archive"
                hideLabel
                accept={ACCEPT_ATTR}
                file={file}
                onPick={pickFile}
                error={fileError}
                disabled={busy}
                dropTitle="Drop the session archive here"
                hint="A single archive, as the recorder wrote it."
                meta={`${ACCEPTED_EXTENSIONS.join(' or ')} · checked after selection`}
              />
            </Card>

            <Button
              variant="primary"
              full
              disabled={!canSubmit}
              busy={flow.step === 'requesting' || flow.step === 'submitting'}
              onClick={() => void start()}
            >
              Upload and process
            </Button>
          </div>

          {/*
            ── THE GUARD IS NOT COSMETIC ──────────────────────────────────────
            This whole block survives into `requesting` and `uploading` --
            `pending` is set only after the upload leg -- so an unguarded list
            would put "Cleaning the signal" and "Scoring against your baseline"
            on screen before any job exists, and the two assertions that prove
            the JOB's phase is rendered would pass without a job. That is a
            behaviour assertion weakened without being touched (R2).

            Canvas line 715's sentence about leaving the page and it picking up
            again is deliberately NOT here: before a job exists there is no
            pointer to pick up, so it would be false. It stays where it is
            true, as the Processing card's `detail` -- and it is quoted nowhere
            else in this file, so grepping the phrase finds exactly the one
            render site.
          */}
          {flow.step === 'idle' && (
            <Card eyebrow="What happens next" className="record-flow__next">
              <ul className="phase-list">
                {Object.entries(PHASE_WORDS).map(([key, words]) => (
                  <li key={key}>{words}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      {flow.step === 'uploading' && (
        <Card eyebrow="Uploading" active>
          <ProgressBar
            label="Sending your recording"
            value={flow.percent}
            detail="Your browser is sending the archive. This does not need the app in front."
          />
        </Card>
      )}

      {flow.step === 'submitting' && !pollRefused && (
        <Card>
          <p className="loading-row" role="status">
            <span className="spinner" aria-hidden="true" /> Handing it to the processor…
          </p>
        </Card>
      )}

      {jobId !== null && !pollRefused && <JobWatch poll={poll} onStartOver={startOver} revealResult={pending?.experiment === undefined} />}

      {recovering && jobId === null && (
        <Card eyebrow="Picking up where you left off" active className="record-recovery">
          {replayStalled ? (
            <Notice tone="warn">
              <p>
                We could not reach the server to pick up your{' '}
                {CONDITION_WORDS[resumeOf.condition].toLowerCase()} upload. It has not been
                lost.
              </p>
              <Button variant="secondary" size="sm" onClick={retryReplay}>
                Check again
              </Button>
            </Notice>
          ) : (
            <p className="loading-row" role="status">
              <span className="spinner" aria-hidden="true" /> Recovering an upload from{' '}
              {CONDITION_WORDS[resumeOf.condition].toLowerCase()}…
            </p>
          )}
          <p className="product-empty__body">
            The last attempt did not report back. Rather than submit it again — which would
            score the same session twice — this replays the original request, which returns
            the job that already exists.
          </p>
          <p className="product-empty__body">
            Starting a new upload instead would submit this session a second time if the
            first one was accepted.
          </p>
          <Button variant="ghost" onClick={startOver}>
            Start a new upload instead
          </Button>
        </Card>
      )}
    </div>
  )
}

/* `messageFor()` stood here and is gone. It returned
   `problem.detail || problem.title` and discarded `problem.code`, which §7.1
   asks for by name -- and which this same file already rendered correctly, as
   `Reference: <code>`, for a terminal job. `ProblemNotice` is that shape,
   shared. */
